import { useContext, useMemo, useReducer } from "react";

import { format } from "date-fns";

import { DialogContext } from "@/components/layout/DialogProvider";
import { CertificationDrawer } from "@/components/pages_logged_in/certifications/CertificationDrawer";
import { ExpirationState } from "@/components/pages_logged_in/certifications/CertificationExpirationForm";
import {
  descriptionMaxLength,
  nameMaxLength,
} from "@/components/pages_logged_in/certifications/CertificationForm";
import { Expiration } from "@/components/pages_logged_in/certifications/types";
import { toExpiration } from "@/components/pages_logged_in/certifications/utils";
import {
  CertificationDetailsDocument,
  useEditCertificationMutation,
} from "@/graphql";

type Action =
  | {
      type: "SET_NAME";
      name: string;
    }
  | {
      type: "SET_DESCRIPTION";
      description: string;
    }
  | {
      type: "SET_EXPIRATION";
      expiration: ExpirationState;
    }
  | {
      type: "SET_ERROR";
      error: string | null;
    };

type State = {
  name: string;
  description: string;
  expiration: ExpirationState;
  preview: {
    name: string;
    description: string;
    expiration: Expiration;
  };
  error: string | null;
};

function editCertificationReducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_NAME":
      return { ...state, name: action.name, error: null };
    case "SET_DESCRIPTION":
      return { ...state, description: action.description, error: null };
    case "SET_EXPIRATION":
      return { ...state, expiration: action.expiration, error: null };
    case "SET_ERROR":
      return { ...state, error: action.error };
  }
}

export function EditCertificationDrawer({
  certificationId,
  initialName,
  initialDescription,
  initialExpiration,
  isOpen,
  onOpenChange,
  workspaceName,
  workspaceLogo,
}: {
  certificationId: string;
  initialName: string;
  initialDescription: string;
  initialExpiration: ExpirationState;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceName: string;
  workspaceLogo: string;
}) {
  const initialState: State = useMemo(() => {
    return {
      name: initialName,
      description: initialDescription,
      expiration: initialExpiration,
      preview: {
        name: initialName,
        description: initialDescription,
        expiration: toExpiration(
          initialExpiration.type,
          initialExpiration.relativeExpirationInterval,
          initialExpiration.relativeExpirationValue,
          initialExpiration.absoluteExpiration,
        ),
      },
      error: null,
    };
  }, [initialName, initialDescription, initialExpiration]);
  const [state, dispatch] = useReducer(editCertificationReducer, initialState);

  const handleNameChange = (name: string) => {
    dispatch({ type: "SET_NAME", name });
  };

  const handleDescriptionChange = (description: string) => {
    dispatch({ type: "SET_DESCRIPTION", description });
  };

  const handleExpirationChange = (expiration: ExpirationState) => {
    dispatch({ type: "SET_EXPIRATION", expiration });
  };

  const isDirty = useMemo(() => {
    return JSON.stringify(state) != JSON.stringify(initialState);
  }, [state, initialState]);

  const disabledText = useMemo(() => {
    if (!isDirty) {
      return "No changes to save";
    }
    return state.name.length === 0 &&
      state.description.length > descriptionMaxLength
      ? `The certification needs a title and the description is too long`
      : state.name.length === 0
        ? `The certification needs a title`
        : state.name.length > nameMaxLength &&
            state.description.length > descriptionMaxLength
          ? `The title and description are too long`
          : state.name.length > nameMaxLength
            ? `The title is too long`
            : state.description.length > descriptionMaxLength
              ? `The description is too long`
              : undefined;
  }, [isDirty, state.name, state.description]);

  const { showConfirmationDialog } = useContext(DialogContext);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (isDirty) {
        showConfirmationDialog({
          title: "Are you sure you want to close this certification?",
          description: "All changes will be lost.",
          intent: "primary",
          onConfirm: () => onOpenChange(open),
          onCancel: () => {},
        });
      } else {
        onOpenChange(open);
      }
    }
  };

  const [editCertification, { loading }] = useEditCertificationMutation();

  const handleSubmit = () => {
    editCertification({
      variables: {
        id: certificationId,
        name: state.name,
        description: state.description,
        expirationType: state.expiration.type,
        expirationInterval: state.expiration.relativeExpirationInterval,
        expirationValue: state.expiration.relativeExpirationValue,
        expirationDate:
          state.expiration.absoluteExpiration &&
          format(new Date(state.expiration.absoluteExpiration), "yyyy-MM-dd"),
      },
      onCompleted: (result) => {
        if (
          result?.updateCertification?.errors &&
          result.updateCertification.errors.length > 0
        ) {
          dispatch({
            type: "SET_ERROR",
            error: result?.updateCertification?.errors
              ?.map((error) => error?.messages?.join(", "))
              .join(", "),
          });
        } else {
          console.log("success");
          onOpenChange(false);
        }
      },
      onError: (error) => {
        dispatch({ type: "SET_ERROR", error: error.message });
      },
      refetchQueries: [
        {
          query: CertificationDetailsDocument,
          variables: { id: certificationId },
        },
        "CertificationParticipants",
      ],
    });
  };

  return (
    <CertificationDrawer
      name={state.name}
      description={state.description}
      expiration={state.expiration}
      preview={state.preview}
      title="Edit Certification"
      workspaceName={workspaceName}
      workspaceLogo={workspaceLogo}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onNameChange={handleNameChange}
      onDescriptionChange={handleDescriptionChange}
      onExpirationChange={handleExpirationChange}
      onSubmit={handleSubmit}
      loading={loading}
      errorMessage={state.error}
      disabledText={disabledText}
    />
  );
}
